import React, { ReactNode, useCallback, useContext, useState } from 'react'
import Row from 'components/Layout/Row'
import { ModalActions } from 'components/Modal'
import { useTranslation } from 'contexts/Localization'
import { Text, Box, Button, AutoRenewIcon } from 'vvs-uikit'
import { CurrencyLogo } from 'components/Logo'
import LocalBalance from 'components/LocalBalance'
import tokens from 'config/constants/tokens'
import xVvsContext from 'views/xVvs/context'
import { formatNumber } from 'utils/formatBalance'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import BigNumber from 'bignumber.js'
import { tryParseAmount } from 'state/swap/hooks'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { useERC20 } from 'hooks/useContract'
import usePriceXvvsUsdc from 'views/xVvs/hooks/usePriceXvvsUsdc'
import useInvalidateBoostedFarms from 'views/Farms/hooks/useInvalidateBoostedFarms'

interface Props {
  remark: ReactNode
  onConfirm: () => void
  pendingTx: boolean
}

const ConfirmStep = ({ onConfirm, pendingTx, remark }: Props) => {
  const { t } = useTranslation()
  const [requestedApproval, setRequestedApproval] = useState(false)

  const xVvsContract = useERC20(tokens.xvvs.address)

  const { state } = useContext(xVvsContext)
  const { value } = state

  const { onApprove } = useVvsBoostHandlers()
  const { boostData } = useUserVvsBoost()
  const { allowance } = boostData
  const { onInvalidatingBoostedFarms } = useInvalidateBoostedFarms()

  const xvvsPriceUsd = usePriceXvvsUsdc()

  const isApproveSpending =
    allowance &&
    allowance.isGreaterThan('0') &&
    allowance.isLessThan(new BigNumber(tryParseAmount(value, tokens.xvvs)?.raw.toString() || 0))

  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      await onApprove(xVvsContract)
      onInvalidatingBoostedFarms()

      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, xVvsContract, onInvalidatingBoostedFarms])

  return (
    <Box p="0 24px">
      <Text small mb={2}>
        {t('Deposit Amount')}
      </Text>
      <Row>
        <CurrencyLogo size="30px" currency={tokens.xvvs} />
        <Box ml="8px" style={{ flex: 1 }}>
          <Row justify="space-between">
            <Text>{formatNumber(parseFloat(value), 2, 6)}</Text>
            <Text>{tokens.xvvs.symbol}</Text>
          </Row>
          <LocalBalance prefix="~ " color="textSubtle" fontSize="9.75px" value={xvvsPriceUsd.times(value)} />
        </Box>
      </Row>
      {remark}
      <ModalActions>
        <Button
          width="100%"
          onClick={isApproveSpending ? handleApprove : onConfirm}
          isLoading={requestedApproval || pendingTx}
          endIcon={requestedApproval || pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
        >
          {isApproveSpending ? t('Approve Spending') : t('Confirm')}
        </Button>
      </ModalActions>
    </Box>
  )
}

export default ConfirmStep
